/* eslint-disable no-shadow */
/* eslint-disable react/jsx-no-bind */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import { Modal, InputText, QuestionIcon, CollapsibleList, Loading } from '../../../../../components';
import { isNullOrEmptyArray } from '../../../../../util/arrays/empty';
import { usePromptConfirm } from '../../../../../hooks';
import { findEvolutiveConfig, storeEvolutiveConfig, updateEvolutiveConfig } from '../../../../../stores/actions/configs';
import {
  MultiSurveysAwareCollapsibleList,
  SingleQuestionAwareCollapsibleList,
  MappedQuestionsAwareCollapsibleList,
} from '../../../../AwareCollapsibleList';
import {
  SingleQuestionAwareCollapsibleOrderableList,
  MultiQuestionAwareCollapsibleOrderableList,
} from '../../../../AwareCollapsibleOrderableList';
import alternativeMatchsMapper from './alternativeMatchsMapper';
import * as Ui from './styles';

// const msgSaveWithSuccess = 'Configuração salva com sucesso';
const errStep1 = 'Defina o título e selecione, pelo menos, uma pesquisa para continuar';
const errStep2 = 'Selecione, pelo menos, uma pergunta para continuar';
const errStep3 = 'Realize as combinações de todas as alternativas com as alternativas da pergunta principal';
const errStep4 = errStep3;
const msgSaveWithSuccess = 'Configuração salva com sucesso';

const titleLength = 100;

function EvolutiveConfig({
  // identidade do cliente
  customerId,
  // identidade da pesquisa principal
  surveyId,
  // título da pesquisa principal
  surveyTitle,
  // identidade da pergunta principal
  questionId,
  // enunciado da pergunta principal
  questionTitle,
  // identidade da configuração inicial
  configId,
  // define se o modal está aberto
  isOpen = false,
  // propaga o evento de acionamento de fechar o modal
  onClose,
  // propaga o evento de acionamento de salvar os dados
  onSave,
  // others
  ...props
}) {
  // refs
  const modalRef = useRef();

  // prompts
  const [openConfirm, renderConfirm] = usePromptConfirm();

  // from redux
  const { dispatch, storeProgress, evolutiveConfig, surveys } = props;

  // states
  const [storing, setStoring] = useState(false);
  const [isShowing, show] = useState(isOpen);
  const [hiddenOkButton, setHiddenOkButton] = useState(true);
  const [selectedContentIndex, setSelectedContentIndex] = useState(0);
  const [selectedSurveysIds, setSelectedSurveysIds] = useState([/* 1, 2, 3 ... */]);
  const [loading, setLoading] = useState(false);
  // states (for edit or insert mode)
  const [editMode, setEditMode] = useState(false);
  const [title, setTitle] = useState('');
  const [othersSurveysQuestions, setOthersSurveysQuestions] = useState([/* {surveyId, surveyTitle, questionId, questionTitle} */]);

  // alternativeMatchs = [{surveyId, questionId, matchs:[{source, matched}]}]
  // surveyId = id da pesquisa equivalente
  // questionId = id da pergunta equivalente
  // source = id da alternativa principal
  // matched = id da alternativa equivalente
  const [alternativeMatchs, setAlternativeMatchs] = useState([/* {surveyId, questionId, matchs:[{source, matched}]} */]);

  const [firstSetpAllSelected, setFirstSetpAllSelected] = useState(false);
  const [secondSetpAllSelected, setSecondSetpAllSelected] = useState(false);
  // estado que monitora os dados de match automático
  const [autoMatchData, setAutoMatchData] = useState(null);

  // callbacks:
  const getSurveysQuestions = useCallback((sId, qId) => {
    let result = null;
    surveys.forEach((s) => {
      if (s.id === sId) {
        s.perguntas.forEach((p) => {
          if (p.id === qId) {
            result = {
              surveyId: s.id,
              surveyTitle: s.titulo,
              questionId: p.id,
              questionTitle: p.enunciado,
            };
          }
        });
      }
    });
    return result;
  }, [surveys]);

  // callback: reinicia todos os dados
  const resetAll = useCallback(() => {
    setTitle('');
    show(false);
    setEditMode(false);
    setSelectedContentIndex(0);
    setSelectedSurveysIds([]);
    setOthersSurveysQuestions([]);
    setAlternativeMatchs([]);
    if (modalRef.current) {
      modalRef.current.selectTab(0);
    }
  }, []);

  // callback: reinicia todos os dados
  const close = useCallback((saving, editing) => {
    resetAll();
    // propagar evento de salvar e fechar
    if (saving && onSave) {
      onSave(editing);
    }
    if (!saving && onClose) {
      onClose();
    }
  }, [resetAll, onClose, onSave]);

  /**
  * Monitora o autoMatchData para atualizar o alternativeMatchs com os dados
  * mapeados automáticamente(caso existam dados mapeados automáticamente)
  */
  useEffect(() => {
    if (autoMatchData !== null && !editMode) {
      setAlternativeMatchs((_alternativeMatchs) => {
        const autoMatched = _alternativeMatchs.map((item) => {
          if (item.surveyId === autoMatchData.surveyId && item.questionId === autoMatchData.questionId) {
            return autoMatchData;
          }
          return item;
        });

        return autoMatched;
      });
    }
  }, [autoMatchData, editMode]);
  // on running
  useEffect(() => {
    const withLoading = (
      storeProgress && storeProgress.running
    ) || (
      evolutiveConfig && evolutiveConfig.running
    );
    setLoading(withLoading);
  }, [evolutiveConfig, storeProgress]);

  /**
   * Carrega o formulário baseado na configuração do evolutivo passado
   * e no callback responsável por recuperar as perguntas de uma dada pesquisa
   * @param {*} evolutiveConfig configuração do evolutivo a ser carregado no form
   * @param {*} getSurveysQuestions callback responsável por recuperar perguntas de uma pesquisa
   */
  function loadEditForm(evolutiveConfig, getSurveysQuestions) {
    /**
     * Setando o título da configuração no formulário
     */
    const configData = evolutiveConfig.data;
    setTitle(configData.descricao);

    /**
     * Setando as pesquisas e perguntas selecionadas
     * no formulário
     */
    const sids = [];
    const qothers = [];
    configData.mapeamento_pesquisas.pesquisa_equivalentes.forEach((p) => {
      // surveysIds
      if (!sids.includes(p.id)) {
        sids.push(p.id);
      }
      // questions
      qothers.push(getSurveysQuestions(p.id, p.id_pergunta));
    });
    setOthersSurveysQuestions(qothers);
    setSelectedSurveysIds(sids);

    /**
     * Setando os matchings entre alternativas na TERCEIRA
     * etapa do cadastro
     */
    // matched alternatives [{source, matched}]}
    const matchs = {};
    configData.mapeamento_pesquisas.alternativas.forEach((altPrincipal) => {
      altPrincipal.alternativas_equivalentes.forEach((altEquivalente) => {
        const match = { source: altPrincipal.id, matched: altEquivalente.id };

        if (!matchs[altEquivalente.id_pesquisa]) {
          matchs[altEquivalente.id_pesquisa] = {};
          matchs[altEquivalente.id_pesquisa][altEquivalente.id_pergunta] = [match];
          return;
        }

        if (!matchs[altEquivalente.id_pesquisa][altEquivalente.id_pergunta]) {
          matchs[altEquivalente.id_pesquisa][altEquivalente.id_pergunta] = [match];
          return;
        }

        matchs[altEquivalente.id_pesquisa][altEquivalente.id_pergunta].push(match);
      });
    });

    const getMatchs = (sId, qId) => {
      if (!matchs[sId] || !matchs[sId][qId]) {
        return [];
      }

      return matchs[sId][qId];
    };

    const alts = [];
    qothers.forEach((q) => {
      alts.push({
        surveyId: q.surveyId,
        questionId: q.questionId,
        matchs: getMatchs(q.surveyId, q.questionId),
      });
    });

    setAlternativeMatchs(alts);
  }

  /**
   * Efeito disparado na inicialização do componente.
   * Escuta alterações nos atributos
   * -> editMode: se a configuração está em modo edição ou cadastro;
   * -> evolutiveConfig: a configuração do evolutivo a ser carregada no form;
   * -> title: titulo da configuração;
   * -> getSurveysQuestions: useCallback responsável por recuperar
   * perguntas de uma pesquisa;
   */
  useEffect(() => {
    if (evolutiveConfig && evolutiveConfig.data && editMode && !title) {
      loadEditForm(evolutiveConfig, getSurveysQuestions);
    }
  }, [editMode, evolutiveConfig, title, getSurveysQuestions]);

  // efeito de alterar a pergunta
  useEffect(() => {
    if (questionId) setHiddenOkButton(false);
  }, [questionId]);

  /**
   * É responsabilidade deste componente controlar a abertura e fechamento
   * do Modal, logo, é melhor controlar essa abertura através do estado
   * ao invés de delegar para o "parent" através de isOpen.
   *
   * Dessa forma foi criado um isShowing, cujo efeito de alteração em isOpen
   * pode ser percebido por este componente. Ficando, dessa forma, o controle
   * de abertura e fechamento sob responsabilidade deste componente.
   *
   * Seguir este raciocínio é recomendado.
   */
  useEffect(() => {
    show(isOpen);
  }, [isOpen]);

  /**
   * Dispara uma chamada para recuperar os dados para entrar no modo de edição
   */
  useEffect(() => {
    if (configId) {
      findEvolutiveConfig(customerId, surveyId, questionId, configId, dispatch);
      setEditMode(true);
    }
  }, [dispatch, configId, customerId, questionId, surveyId]);

  /**
   * No caso de haver erro depois que solicitar salvar
   */
  useEffect(() => {
    if (storeProgress.running && !storing) { // start processing
      setStoring(true);
    } else if (!storeProgress.running && storing && storeProgress.error) { // proccessed with fail
      toast.error(storeProgress.error.toString());
      setStoring(false);
    } else if (!storeProgress.running && storing) { // proccessed with success
      toast.success(msgSaveWithSuccess);
      setStoring(false);
      close(true, editMode);
    }
  }, [storeProgress, storing, editMode, close]);

  //
  function checkAndSelectContentIndex(index, reverse) {
    if (index > 2) {
      if (alternativeMatchs.length < 1 || othersSurveysQuestions.length !== alternativeMatchs.length) {
        toast.error(errStep3);
        reverse();
        return;
      }
    } else if (index > 1) {
      if (isNullOrEmptyArray(othersSurveysQuestions)) {
        toast.error(errStep2);
        reverse();
        return;
      }
    } else if (index > 0) {
      if (!title || isNullOrEmptyArray(selectedSurveysIds)) {
        toast.error(errStep1);
        reverse();
        return;
      }
    }
    // set selected content index
    setSelectedContentIndex(index);
  }

  function handleSelectTab(index, reverseCallback) {
    if (!customerId || !surveyId || !questionId) {
      reverseCallback(true);
    } else {
      checkAndSelectContentIndex(index, reverseCallback);
    }
  }

  function handleSave() {
    // verificar se houve combinações (pelo menos 1)
    if (alternativeMatchs.length < 1 || othersSurveysQuestions.length !== alternativeMatchs.length) {
      toast.error(errStep4);
      return;
    }
    // confirmar salvar
    openConfirm(() => {
      // mapeando o match de alternativas para a estrutura esperada no paylaod da request
      const mapeamentoAlternativasPayload = alternativeMatchsMapper
        .mapToRequestPayload(alternativeMatchs);
      // title, {surveyId, questionId}, {alternativeId, alternativeTitle, match:[]}
      if (editMode) {
        updateEvolutiveConfig(customerId, surveyId, questionId, configId, title, othersSurveysQuestions, mapeamentoAlternativasPayload, dispatch);
      } else {
        storeEvolutiveConfig(customerId, surveyId, questionId, title, othersSurveysQuestions, mapeamentoAlternativasPayload, dispatch);
      }
    });
  }

  // manipula a navegação entre as telas de passos verificando a validade de cada um
  function handleNextTab() {
    // keep latest content index
    const tmpIndex = selectedContentIndex;
    const newIndex = selectedContentIndex + 1;
    // check and select next tab or rever
    modalRef.current.selectTab(newIndex);
    checkAndSelectContentIndex(newIndex, () => {
      modalRef.current.selectTab(tmpIndex);
    });
  }

  // Verifica o passo atual
  function isCurrentStep(step) {
    return selectedContentIndex === step - 1;
  }

  // handle next or save
  function handleNextOrSave() {
    if (isCurrentStep(3)) {
      handleSave();
    } else {
      handleNextTab();
    }
  }

  // handle close modal
  function handleCancel() {
    close(false, false);
  }

  function handleChangeTitle(value) {
    setTitle(value);
  }

  const handleChangeSelectedSurveys = useCallback((sIds, allSel) => {
    setFirstSetpAllSelected(allSel);
    // cleaning secondStep data
    setSecondSetpAllSelected(false);
    setOthersSurveysQuestions([]);
    // select surveys
    setSelectedSurveysIds(sIds);
    // filter questions
    setOthersSurveysQuestions((_othersSurveysQuestions) => _othersSurveysQuestions.filter((sq) => (
      sIds.includes(sq.surveyId)
    )));
    // filter matchedAlternatives
    // const aSel = alternativeMatchs.filter((am) => (
    //   sIds.includes(am.surveyId)
    // ));
    setAlternativeMatchs((_alternativeMatchs) => _alternativeMatchs.filter((am) => (
      sIds.includes(am.surveyId)
    )));
  }, []);

  const handleChangeSelectedQuestionsCallback = useCallback((questions, allSel) => {
    setSecondSetpAllSelected(allSel);
    // select questions
    setOthersSurveysQuestions(questions);
    // filter matchedAlternatives
    setAlternativeMatchs((_alternativeMatchs) => {
      const aSel = _alternativeMatchs.filter((am) => {
        let result = false;
        questions.forEach((qs) => {
          if (qs.surveyId === am.surveyId && qs.questionId === am.questionId) {
            result = true;
          }
        });
        return result;
      });
      return aSel;
    });
  }, []);

  const handleChangeMatchedAlternativesCallback = React.useCallback((sId, qId, matchs) => {
    const payload = {
      surveyId: sId,
      questionid: qId,
      matchs,
    };

    setAlternativeMatchs((_alternativeMatchs) => {
      const filtered = _alternativeMatchs.filter((a) => !(a.surveyId === sId && a.questionId === qId));
      return [...filtered, { surveyId: sId, questionId: qId, matchs }];
    });
  }, []);

  /**
   * lida com o match automático de dados
   * atualizando o valor de match automático
   */
  function handleAutoMatch(matchData) {
    setAutoMatchData(matchData);
  }

  function renderTabIndex0(visible) {
    return visible ? (
      <Ui.TabContainer className={visible ? 'tab-visible' : 'tab-gone'}>
        <Ui.TitleGroup>
          <Ui.Label>{'Título da Configuração'}</Ui.Label>
          <InputText onChange={handleChangeTitle} value={title} maxLength={titleLength} />
        </Ui.TitleGroup>
        <Ui.ItemsGroup>
          {/*  */}
          <Ui.FixedItem>
            <Ui.Label>
              {'Avaliação principal'}
            </Ui.Label>
            <CollapsibleList
              checkedTitle
              boldedTitle
              title={surveyTitle}
              expansible={false}
              checkable={false}
              withArrow={false}
              textPadding={'5px 20px 5px 15px'}
            />
          </Ui.FixedItem>
          <Ui.DynamicItems bodySectionHeight={'151px'}>
            <MultiSurveysAwareCollapsibleList
              surveysToIgnore={[surveyId]} // remove main
              selectedSurveys={selectedSurveysIds} // memory
              label={'Selecione as avaliações que irão compor o evolutivo'}
              onSelectSurveys={handleChangeSelectedSurveys}
              withAllSelected // Opção de selecionar todos habilitada
              allItemsSelected={firstSetpAllSelected}
            />
          </Ui.DynamicItems>
        </Ui.ItemsGroup>
      </Ui.TabContainer>
    ) : null;
  }

  function renderTabIndex1(visible) {
    return visible ? (
      <Ui.TabContainer className={visible ? 'tab-visible' : 'tab-gone'}>
        <Ui.ItemsGroup>
          {/*  */}
          <Ui.FixedItem>
            <Ui.Label>
              {'Pergunta principal'}
            </Ui.Label>
            <SingleQuestionAwareCollapsibleList
              checkedTitle
              boldedTitle
              customerId={customerId}
              surveyId={surveyId}
              questionId={questionId}
              questionTitle={questionTitle}
              expansible
            />
          </Ui.FixedItem>
          <Ui.DynamicItems>
            <MappedQuestionsAwareCollapsibleList
              // ref={selectorRef}
              customerId={customerId}
              surveysIds={selectedSurveysIds} // filter
              label={'Selecione as perguntas para relacionar a alternativa'}
              questionsToIgnore={[{ surveyId, questionId }]} // remove main (???)
              selectedSurveysQuestions={othersSurveysQuestions} // memory
              onSelectQuestions={handleChangeSelectedQuestionsCallback}
              withAllSelected
              allItemsSelected={secondSetpAllSelected}
            />
          </Ui.DynamicItems>
        </Ui.ItemsGroup>
      </Ui.TabContainer>
    ) : null;
  }

  function renderTabIndex2(visible) {
    return visible ? (
      <Ui.TabContainer className={visible ? 'tab-visible' : 'tab-gone'}>
        <Ui.ItemsGroup>
          {/*  */}
          <Ui.FixedItem>
            <Ui.Label>
              {'Pergunta principal'}
            </Ui.Label>
            <SingleQuestionAwareCollapsibleOrderableList
              checkedTitle
              boldedTitle
              customerId={customerId}
              surveyId={surveyId}
              questionId={questionId}
              questionTitle={questionTitle}
            />
          </Ui.FixedItem>
          <Ui.DynamicItems className="Ui.DynamicItems" bodySectionHeight="150px">
            <Ui.Label>
              {'Combine numericamente as alternativas da questões abaixo com as alternativas da pergunta principal'}
            </Ui.Label>
            <MultiQuestionAwareCollapsibleOrderableList
              customerId={customerId}
              surveyId={surveyId}
              questionId={questionId}
              surveysQuestions={othersSurveysQuestions} // filter
              matchedAlternatives={alternativeMatchs} // memory
              autoMatch={!editMode}
              onChangeMatchedAlternatives={handleChangeMatchedAlternativesCallback}
              onAutoMatch={handleAutoMatch}
            // initialOrder
            />
          </Ui.DynamicItems>
        </Ui.ItemsGroup>
      </Ui.TabContainer>
    ) : null;
  }

  function renderByTabIndex(index) {
    return isShowing ? (
      <Ui.Container>
        {/* 1st step */}
        {renderTabIndex0(index === 0)}
        {/* 2nd step */}
        {renderTabIndex1(index === 1)}
        {/* 3th step */}
        {renderTabIndex2(index === 2)}
      </Ui.Container>
    ) : null;
  }

  // renderizar uma mensagem de erro
  function renderError() {
    return (
      <span className="errorMessage">
        Para realizar uma configuração é necessário selecionar antes a pergunta principal
      </span>
    );
  }

  function renderModalBody() {
    if (loading) {
      return (<Loading size={30} loading />);
    }
    if (customerId && surveyId && questionId) {
      return renderByTabIndex(selectedContentIndex);
    }
    return renderError();
  }

  return (
    <Ui.Wrapper overFlowY={isCurrentStep(3) ? 'scroll' : 'hidden'}>
      <Modal
        ref={modalRef}
        isOpen={isShowing}
        isLoading={loading}
        title={'Configuração de resultados evolutivos'}
        okBtnTitle={isCurrentStep(3) ? 'Salvar' : 'Próximo'}
        cancelBtnTitle={'Cancelar'}
        onOk={handleNextOrSave}
        onCancel={handleCancel}
        hiddenOkButton={hiddenOkButton}
        tabs={['Passo 1', 'Passo 2', 'Passo 3']}
        onSelectTab={(index, reverse) => handleSelectTab(index, reverse)}
      >
        {renderModalBody()}
        {renderConfirm(QuestionIcon, 'Deseja salvar esta configuração?')}
      </Modal>
    </Ui.Wrapper>
  );
}

const mapStateToProps = (state) => {
  const configState = state.evolutive.configuration;
  const surveysState = state.surveys;
  const { evolutiveConfig } = state.configs;
  return {
    // following store action
    storeProgress: {
      running: configState.running,
      error: configState.error,
    },
    // following find action
    evolutiveConfig: {
      running: evolutiveConfig.running,
      error: evolutiveConfig.error,
      data: evolutiveConfig.payload ? evolutiveConfig.payload : null,
    },
    // following surveys
    surveys: surveysState.payload,
  };
};

export default connect(mapStateToProps)(EvolutiveConfig);
